import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SeasonDateRangeInput from './SeasonDateRangeInput';
import type { SeasonDateRange, SeasonalPricingConfig, SeasonalRates } from '../../types';
import {
  DEFAULT_SEASON_RANGES,
  generateSeasonRangeId,
  createDefaultRates,
} from '../../utils/seasonUtils';

interface SeasonalPricingEditorProps {
  config: SeasonalPricingConfig | undefined;
  onChange: (config: SeasonalPricingConfig) => void;
}

const SeasonalPricingEditor: React.FC<SeasonalPricingEditorProps> = ({
  config,
  onChange,
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  // Initialize with default config if not present
  const currentConfig: SeasonalPricingConfig = config || {
    residenceType: 'summer',
    highSeasonRanges: DEFAULT_SEASON_RANGES.summer.map(r => ({
      ...r,
      id: generateSeasonRangeId(),
    })),
    highSeasonRates: createDefaultRates(200),
    lowSeasonRates: createDefaultRates(150),
  };

  const addHighSeasonRange = () => {
    onChange({
      ...currentConfig,
      highSeasonRanges: [
        ...currentConfig.highSeasonRanges,
        { id: generateSeasonRangeId(), startMonth: 6, startDay: 1, endMonth: 8, endDay: 31, label: { de: '', en: '' } },
      ],
    });
  };

  const updateHighSeasonRange = (index: number, range: SeasonDateRange) => {
    const newRanges = [...currentConfig.highSeasonRanges];
    newRanges[index] = range;
    onChange({ ...currentConfig, highSeasonRanges: newRanges });
  };

  const deleteHighSeasonRange = (index: number) => {
    onChange({ ...currentConfig, highSeasonRanges: currentConfig.highSeasonRanges.filter((_, i) => i !== index) });
  };

  const lowSeasonRanges = currentConfig.lowSeasonRanges ?? [];

  const addLowSeasonRange = () => {
    onChange({
      ...currentConfig,
      lowSeasonRanges: [
        ...lowSeasonRanges,
        { id: generateSeasonRangeId(), startMonth: 4, startDay: 1, endMonth: 6, endDay: 30, label: { de: '', en: '' } },
      ],
    });
  };

  const updateLowSeasonRange = (index: number, range: SeasonDateRange) => {
    const newRanges = [...lowSeasonRanges];
    newRanges[index] = range;
    onChange({ ...currentConfig, lowSeasonRanges: newRanges });
  };

  const deleteLowSeasonRange = (index: number) => {
    onChange({ ...currentConfig, lowSeasonRanges: lowSeasonRanges.filter((_, i) => i !== index) });
  };

  const updateWeekRate = (season: 'high' | 'low', value: number) => {
    const rates: SeasonalRates = { weekdayRate: value, weekendRate: value, weekRate: value, familyRate: value, guestRate: value };
    onChange({
      ...currentConfig,
      ...(season === 'high' ? { highSeasonRates: rates } : { lowSeasonRates: rates }),
    });
  };

  const WeekRateEditor: React.FC<{ title: string; rates: SeasonalRates; season: 'high' | 'low'; variant: 'high' | 'low' }> = ({ title, rates, season, variant }) => (
    <Card className={variant === 'high' ? 'border-orange-200 bg-orange-50/50' : 'border-blue-200 bg-blue-50/50'}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Label className="text-xs">Nachtpreis (CHF)</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">CHF</span>
            <Input
              type="number"
              value={rates.weekRate}
              onChange={(e) => updateWeekRate(season, Number(e.target.value))}
              className="pl-12"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* High Season Ranges */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>{t('pricing.highSeasonRanges')}</Label>
          <Button type="button" variant="outline" size="sm" onClick={addHighSeasonRange}>
            <Plus className="h-4 w-4 mr-1" />
            {t('pricing.addRange')}
          </Button>
        </div>

        {currentConfig.highSeasonRanges.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
            {t('pricing.noSeasonRanges')}
          </div>
        ) : (
          <div className="space-y-3">
            {currentConfig.highSeasonRanges.map((range, index) => (
              <SeasonDateRangeInput
                key={range.id}
                range={range}
                onChange={(newRange) => updateHighSeasonRange(index, newRange)}
                onDelete={() => deleteHighSeasonRange(index)}
                language={language}
              />
            ))}
          </div>
        )}
      </div>

      {/* Low Season Ranges */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Nebensaison Zeiträume</Label>
          <Button type="button" variant="outline" size="sm" onClick={addLowSeasonRange}>
            <Plus className="h-4 w-4 mr-1" />
            {t('pricing.addRange')}
          </Button>
        </div>

        {lowSeasonRanges.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg text-sm">
            Noch keine Nebensaison-Zeiträume definiert
          </div>
        ) : (
          <div className="space-y-3">
            {lowSeasonRanges.map((range, index) => (
              <SeasonDateRangeInput
                key={range.id}
                range={range}
                onChange={(newRange) => updateLowSeasonRange(index, newRange)}
                onDelete={() => deleteLowSeasonRange(index)}
                language={language}
              />
            ))}
          </div>
        )}
      </div>

      {/* Rates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WeekRateEditor
          title={t('pricing.highSeasonRates')}
          rates={currentConfig.highSeasonRates}
          season="high"
          variant="high"
        />
        <WeekRateEditor
          title={t('pricing.lowSeasonRates')}
          rates={currentConfig.lowSeasonRates}
          season="low"
          variant="low"
        />
      </div>
    </div>
  );
};

export default SeasonalPricingEditor;
